import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import AutoScroll from 'embla-carousel-auto-scroll';
import { Skeleton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTrackImpression } from '@/analytics/useTrackImpression';
import {
  getSurfacesQueryKey,
  surfaceDisplayMode,
  useRecordImpression,
  useSurfaces,
  type SurfaceItem,
} from '@/lib/monetization';
import { SurfaceCard } from './SurfaceCard';
import { SurfaceDetailSheet } from './SurfaceDetailSheet';
import { SurfaceReminder } from './SurfaceReminder';

export interface MonetizationBarProps {
  className?: string;
  /** Gate the fetch on auth — guests have no resolved surfaces. */
  enabled?: boolean;
}

/**
 * Horizontal lobby strip of monetization surfaces (`GET /monetization/surfaces`).
 * Renders the GIFT / OFFER / MESSAGE surfaces as compact promo widgets (Figma:
 * Widget) in an auto-scrolling Embla carousel — auto-scroll only kicks in when
 * the widgets overflow the viewport. Tapping a widget opens a detail sheet that
 * carries the claim / deposit action. Fires a backend impression the first time
 * each widget scrolls into view, and renders nothing when there are no surfaces.
 */
export function MonetizationBar({ className, enabled = true }: MonetizationBarProps) {
  const { data, isLoading } = useSurfaces({ query: { enabled } });
  const items = data ?? [];
  const [openItem, setOpenItem] = useState<SurfaceItem | null>(null);
  // FULL_POPUP surfaces already auto-opened during the current auto-display
  // window. Pruned when a surface stops being auto-eligible (leaves the feed OR
  // enters its display cooldown, i.e. `autoDisplay === false`), so once the
  // cooldown lifts it can auto-open again — the cooldown, not this guard, is the
  // re-pop throttle. The widget itself stays in the feed the whole time.
  const autoOpened = useRef<Set<string>>(new Set());
  // REMINDER surfaces dismissed/opened by the player; likewise pruned when the
  // surface stops being auto-eligible so it can return after the cooldown.
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const queryClient = useQueryClient();

  // Persist a dismissal/close by flipping the surface's `autoDisplay` off in the
  // surfaces cache. The per-mount `dismissed` Set and `autoOpened` ref reset
  // when the lobby (Trending → MonetizationBar) unmounts, so on their own they
  // can't stop a surface re-popping after the player navigates away and back —
  // React Query serves the still-`autoDisplay:true` cached feed and the
  // auto-open effect below fires again. Writing the suppression into the cached
  // surface makes the dismissal outlive the unmount; the server's display
  // cooldown then keeps `autoDisplay` false on the next refetch until it lifts
  // (after which the surface is free to auto-surface again).
  const suppressAutoDisplay = useCallback(
    (id: string) => {
      queryClient.setQueryData<SurfaceItem[]>(getSurfacesQueryKey(), (prev) =>
        prev?.map((it) => (it.experienceId === id ? { ...it, autoDisplay: false } : it)),
      );
    },
    [queryClient],
  );

  const dismissReminder = useCallback(
    (id: string) => {
      setDismissed((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
      suppressAutoDisplay(id);
    },
    [suppressAutoDisplay],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: true }, [
    AutoScroll({
      playOnInit: true,
      speed: 0.3,
      startDelay: 0,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  ]);

  // Auto-scroll only when the widgets actually overflow the track — a short
  // strip (1–2 widgets) stays still. Ported from the original OfferCardsContainer.
  useEffect(() => {
    if (!emblaApi) return;
    const autoScroll = emblaApi.plugins().autoScroll;
    if (!autoScroll) return;

    const update = () => {
      const slides = emblaApi.slideNodes();
      const root = emblaApi.rootNode();
      if (!slides.length) return autoScroll.stop();
      const minSlideWidth = Math.min(...slides.map((s) => s.offsetWidth));
      const totalContent = slides.reduce((sum, s) => sum + s.offsetWidth, 0);
      const fullyHidden = totalContent - root.clientWidth >= minSlideWidth;
      if (fullyHidden) autoScroll.play();
      else autoScroll.stop();
    };

    update();
    emblaApi.on('reInit', update);
    window.addEventListener('resize', update);

    return () => {
      emblaApi.off('reInit', update);
      window.removeEventListener('resize', update);
    };
  }, [emblaApi]);

  // On return to the lobby, auto-open the detail sheet for the first FULL_POPUP
  // surface that may auto-display now (highest priority — the feed is
  // pre-ordered server-side). Runs when the feed changes; the `autoOpened` guard
  // stops it reopening a surface the player just closed while it's still
  // auto-eligible. ON_CLICK never auto-surfaces; REMINDER gets the bottom bar
  // below instead. A surface inside its display cooldown (`autoDisplay === false`)
  // stays in the feed (the widget is always visible) but does not auto-surface.
  useEffect(() => {
    // Auto-eligible = present in the feed AND not inside its display cooldown.
    // Forgetting the rest lets a surface auto-surface again once its cooldown
    // lifts (autoDisplay flips back to true).
    const autoEligible = new Set(items.filter((it) => it.autoDisplay !== false).map((it) => it.experienceId));
    for (const id of autoOpened.current) if (!autoEligible.has(id)) autoOpened.current.delete(id);
    setDismissed((prev) => {
      const next = new Set([...prev].filter((id) => autoEligible.has(id)));
      return next.size === prev.size ? prev : next;
    });

    const pop = items.find(
      (it) =>
        surfaceDisplayMode(it) === 'FULL_POPUP' && it.autoDisplay !== false && !autoOpened.current.has(it.experienceId),
    );
    if (pop) {
      autoOpened.current.add(pop.experienceId);
      setOpenItem(pop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // The top REMINDER surface that may auto-display now and isn't yet
  // dismissed/opened — shown as a bottom bar, but only while no detail sheet is
  // open (so the two never stack). A reminder inside its display cooldown
  // (`autoDisplay === false`) is suppressed; its widget still rides in the feed.
  const reminderItem =
    openItem == null
      ? items.find(
          (it) => surfaceDisplayMode(it) === 'REMINDER' && it.autoDisplay !== false && !dismissed.has(it.experienceId),
        )
      : undefined;

  if (enabled && isLoading) return <MonetizationBarSkeleton className={className} />;
  if (items.length === 0) return null;

  return (
    <section aria-label="Offers and gifts" className={className}>
      <div className="overflow-hidden pl-[14px]" ref={emblaRef} role="region" aria-label="Promotions and offers">
        <div className="flex">
          {items.map((item) => (
            <SurfaceSlide key={item.experienceId} item={item} onOpen={() => setOpenItem(item)} />
          ))}
        </div>
      </div>
      <SurfaceDetailSheet
        item={openItem}
        onClose={() => {
          // Closing the sheet is a dismissal too: suppress the surface's
          // auto-nudge so an auto-opened FULL_POPUP won't re-open on the next
          // lobby return until its display cooldown lifts.
          if (openItem) suppressAutoDisplay(openItem.experienceId);
          setOpenItem(null);
        }}
      />
      {reminderItem && (
        <SurfaceReminder
          item={reminderItem}
          onOpen={() => {
            dismissReminder(reminderItem.experienceId);
            setOpenItem(reminderItem);
          }}
          onDismiss={() => dismissReminder(reminderItem.experienceId)}
        />
      )}
    </section>
  );
}

/**
 * One Embla slide wrapping a {@link SurfaceCard}. The slide element doubles as
 * the impression target: when it scrolls ≥50% into view it records the backend
 * impression, de-duped per session by {@link useTrackImpression}.
 */
function SurfaceSlide({ item, onOpen }: { item: SurfaceItem; onOpen: () => void }) {
  const recordImpression = useRecordImpression();

  const ref = useTrackImpression(`monetization:${item.experienceId}:${item.variationKey}`, () =>
    recordImpression(item.experienceId),
  );

  return (
    <div ref={ref} className="min-w-0 shrink-0 pr-[14px]">
      <SurfaceCard item={item} onOpen={onOpen} />
    </div>
  );
}

function MonetizationBarSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('w-full overflow-hidden pl-[14px]', className)}>
      <div className="flex gap-[14px]">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-[74px] w-[150px] shrink-0 rounded-xl" />
        ))}
      </div>
    </div>
  );
}
